import { useCallback, useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useAuth } from "@/contexts/AuthContext";
import { listManufacturingPlants } from "@/lib/manufacturingClient";
import { canDeleteManufacturingMaterialUse } from "@/lib/manufacturing/modelDeleteAccess";
import type { MfgMaterial, MfgPlant, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingMaterialUseDetailEditor } from "./ManufacturingMaterialUseDetailEditor";
import { ManufacturingMaterialUseExplainer } from "./ManufacturingMaterialUseExplainer";
import { ManufacturingFinishVariantDeficitsPanel } from "./ManufacturingFinishVariantDeficitsPanel";
import { ManufacturingMaterialUsesBulkImportModal } from "./ManufacturingMaterialUsesBulkImportModal";
import { ManufacturingMaterialUsesListCard } from "./ManufacturingMaterialUsesListCard";
import { useEntityBulkSelection } from "./useEntityBulkSelection";
import { useManufacturingMaterialUsesExport } from "./useManufacturingMaterialUsesExport";
import { useManufacturingMaterialUsesListView } from "./useManufacturingMaterialUsesListView";
import { useManufacturingMaterialUsesTab } from "./useManufacturingMaterialUsesTab";
import { useMaterialUseDeleteFlow } from "./useMaterialUseDeleteFlow";

function confirmLeavePartUse(opts: {
  createDirty: boolean;
  editDirty: boolean;
  pendingConvertLines: number;
}): boolean {
  if (opts.createDirty) {
    return window.confirm("You have an unsaved part use. Leave without saving?");
  }
  if (opts.editDirty && opts.pendingConvertLines > 0) {
    return window.confirm(
      "You have unsaved changes, and BOM lines still need Convert. Leave anyway?",
    );
  }
  if (opts.editDirty) {
    return window.confirm("You have unsaved changes to this part use. Leave without saving?");
  }
  if (opts.pendingConvertLines > 0) {
    return window.confirm(
      `${opts.pendingConvertLines} BOM line${
        opts.pendingConvertLines === 1 ? "" : "s"
      } still pin member materials. Convert them before leaving, or leave anyway?`,
    );
  }
  return true;
}

export function ManufacturingMaterialUsesPanel({
  enabled,
  refreshTick,
  materials = [],
  recipes = [],
  onLeaveGuardChange,
  focusUseId = null,
  onFocusUseApplied,
  onDeficitsChanged,
}: {
  enabled: boolean;
  refreshTick: number;
  materials?: readonly MfgMaterial[];
  recipes?: readonly MfgRecipeSummary[];
  /** Returns false when the operator cancels leaving Part uses. */
  onLeaveGuardChange?: (guard: (() => boolean) | null) => void;
  /** Open this part use when set (e.g. Variant deficits control). */
  focusUseId?: string | null;
  onFocusUseApplied?: () => void;
  onDeficitsChanged?: () => void;
}) {
  const auth = useAuth();
  const canDelete =
    auth?.status === "ready" && canDeleteManufacturingMaterialUse((...r) => auth.hasRole(...r));
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [plantId, setPlantId] = useState<string>("");
  const [pendingConvertLines, setPendingConvertLines] = useState(0);
  const u = useManufacturingMaterialUsesTab(enabled, refreshTick, plantId || null);
  const listView = useManufacturingMaterialUsesListView(u.uses, u.memberSearchByUseId);
  const bulk = useEntityBulkSelection(u.uses, listView.filtered);
  const exp = useManufacturingMaterialUsesExport();
  const [showBulkImport, setShowBulkImport] = useState(false);

  const handleDeleted = useCallback(() => {
    u.closeEditor();
    void u.reloadList();
  }, [u.closeEditor, u.reloadList]);

  const deleteFlow = useMaterialUseDeleteFlow({ onDeleted: handleDeleted });

  useEffect(() => {
    if (!enabled) {
      setPlants([]);
      return;
    }
    void (async () => {
      const r = await listManufacturingPlants();
      if (!r.ok) return;
      const list = r.data.plants.filter((p) => p.is_active);
      setPlants(list);
      if (!plantId && list.length > 0) {
        const def = list.find((p) => p.is_default) ?? list[0];
        setPlantId(def.id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed plant once
  }, [enabled, refreshTick]);

  useEffect(() => {
    if (!u.selectedId) setPendingConvertLines(0);
  }, [u.selectedId]);

  const plantMaterials = useMemo(() => {
    if (!plantId) return materials;
    return materials.filter((m) => m.plant_id === plantId && m.is_active);
  }, [materials, plantId]);

  const memberMaterialIds = useMemo(
    () => new Set(u.members.map((m) => m.material_id)),
    [u.members],
  );

  const convertMemberMaterialIds = useMemo(
    () => u.members.filter((m) => m.is_active).map((m) => m.material_id),
    [u.members],
  );

  const editorOpen = u.creating || Boolean(u.selectedId);

  const allowLeave = useCallback(() => {
    return confirmLeavePartUse({
      createDirty: u.createDirty,
      editDirty: u.editDirty,
      pendingConvertLines,
    });
  }, [u.createDirty, u.editDirty, pendingConvertLines]);

  useEffect(() => {
    onLeaveGuardChange?.(allowLeave);
    return () => onLeaveGuardChange?.(null);
  }, [allowLeave, onLeaveGuardChange]);

  useEffect(() => {
    const dirtyLeave = u.createDirty || u.editDirty || pendingConvertLines > 0;
    if (!dirtyLeave) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [u.createDirty, u.editDirty, pendingConvertLines]);

  function guardedBeginCreate() {
    if (!allowLeave()) return;
    u.beginCreate();
  }

  function guardedPickUse(id: string) {
    if (id === u.selectedId && !u.creating) return;
    if (!allowLeave()) return;
    u.pickUse(id);
  }

  function guardedCloseEditor() {
    if (!allowLeave()) return;
    u.closeEditor();
  }

  useEffect(() => {
    if (!focusUseId) return;
    if (focusUseId !== u.selectedId || u.creating) {
      if (!allowLeave()) {
        onFocusUseApplied?.();
        return;
      }
      u.pickUse(focusUseId);
    }
    onFocusUseApplied?.();
    // Only react to focusUseId arrivals; leave-guard uses latest allowLeave/pickUse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusUseId]);

  return (
    <div className="space-y-4">
      {u.selectedId && !u.creating ? (
        <ManufacturingFinishVariantDeficitsPanel
          enabled={enabled}
          plantId={plantId || null}
          refreshTick={refreshTick}
          filterUseId={u.selectedId}
          hideWhenEmpty
          onChanged={onDeficitsChanged}
        />
      ) : null}

      {u.uses.length === 0 && !u.creating ? (
        <ManufacturingMaterialUseExplainer
          variant="uses-empty"
          action={
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              onClick={guardedBeginCreate}
              disabled={!enabled || u.saveBusy}
            >
              + Create first use
            </button>
          }
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <ManufacturingMaterialUsesListCard
          enabled={enabled}
          saveBusy={u.saveBusy}
          listBusy={u.listBusy}
          uses={u.uses}
          selectedId={u.selectedId}
          creating={u.creating}
          listView={listView}
          bulk={bulk}
          exportBusy={exp.busy}
          exportError={exp.error}
          onBeginCreate={guardedBeginCreate}
          onBulkImport={() => setShowBulkImport(true)}
          onReload={() => void u.reloadList()}
          onPickUse={guardedPickUse}
          onExportSelected={() => void exp.exportCsv(bulk.selectedItems.map((x) => x.id))}
          onExportAllFiltered={() => void exp.exportCsv(listView.filtered.map((x) => x.id))}
        />

        <SectionCard
          title={u.creating ? "New part use" : u.selectedUse ? u.selectedUse.name : "Use detail"}
          description={
            u.creating
              ? "Name the shop-floor job, then add interchangeable plant materials."
              : "Edit the job definition and which plant SKUs can fill it."
          }
        >
          <ManufacturingMaterialUseDetailEditor
            creating={u.creating}
            editorOpen={editorOpen}
            saveBusy={u.saveBusy}
            localError={u.localError}
            onError={u.setLocalError}
            create={{
              newName: u.newName,
              setNewName: u.setNewName,
              newCode: u.newCode,
              setNewCode: u.setNewCode,
              setNewCodeTouched: u.setNewCodeTouched,
              newUnit: u.newUnit,
              setNewUnit: u.setNewUnit,
              newNotes: u.newNotes,
              setNewNotes: u.setNewNotes,
              newResolveMode: u.newResolveMode,
              setNewResolveMode: u.setNewResolveMode,
              newFinishChannel: u.newFinishChannel,
              setNewFinishChannel: u.setNewFinishChannel,
              saveCreate: () => void u.saveCreate(),
              closeEditor: guardedCloseEditor,
            }}
            edit={{
              editName: u.editName,
              setEditName: u.setEditName,
              editCode: u.editCode,
              setEditCode: u.setEditCode,
              editUnit: u.editUnit,
              setEditUnit: u.setEditUnit,
              editNotes: u.editNotes,
              setEditNotes: u.setEditNotes,
              editActive: u.editActive,
              setEditActive: u.setEditActive,
              editResolveMode: u.editResolveMode,
              setEditResolveMode: u.setEditResolveMode,
              editFinishChannel: u.editFinishChannel,
              setEditFinishChannel: u.setEditFinishChannel,
              variantOnHandQty: u.variantOnHandQty,
              openDeficitCount: u.openDeficitCount,
              saveEdit: () => void u.saveEdit(),
              selectedUse: u.selectedUse,
              members: u.members,
              detailBusy: u.detailBusy,
              plantId,
              setPlantId,
              plants,
              plantMaterials,
              recipes,
              memberMaterialIds,
              convertMemberMaterialIds,
              onPendingConvertChange: setPendingConvertLines,
              addMember: (id) => void u.addMember(id),
              addMembers: (ids) => void u.addMembers(ids),
              updateMember: (id, patch) => void u.updateMember(id, patch),
              removeMember: (id) => void u.removeMember(id),
              canDelete,
              deletePending: deleteFlow.pending,
              onRequestDelete: deleteFlow.request,
              onConfirmDelete: () => void deleteFlow.confirm(),
              onCancelDelete: deleteFlow.cancel,
            }}
          />
        </SectionCard>
      </div>

      {showBulkImport ? (
        <ManufacturingMaterialUsesBulkImportModal
          existingUses={u.uses}
          materials={materials}
          onClose={() => setShowBulkImport(false)}
          onImported={() => {
            bulk.clearSelection();
            void u.reloadList();
          }}
        />
      ) : null}
    </div>
  );
}
